import React, {useEffect, useMemo, useState} from 'react';
import {Tabs} from 'antd-mobile';
import {useTranslation} from 'react-i18next';
import {cn, formatDate, toPriceStr} from '@/utils';
import {getTeamReportData} from '@/common-pages/proxy/proxy.service';
import {TeamReportPanelContent} from '@/common-pages/proxy/proxy.type';
import PanelSmallItem from '../components/panel-small-item';
import styles from './team-report-panel-tab.module.scss';
import {
  HomeNewUserIcon,
  HomeBetUserIcon,
  HomeRechargeIcon,
  HomeBettingIcon,
} from '@/common-pages/proxy/svg.variable';
import {DateFilter} from '../components/filter';
import {ProgressiveCircularLoading} from '@fe-progressive/package-progressive/progressive-loading';

const TeamReportPanel: React.FC = () => {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<number>(Date.now());
  const [endDate, setEndDate] = useState<number>(Date.now());
  const [filterValue, setFilterValue] = useState<string>('0');
  const [panel, setPanel] = useState<TeamReportPanelContent>();

  const tabOptions = useMemo(() => {
    return [
      {
        key: '0',
        title: t('agent.team-report.tabs.today'),
      },
      {
        key: '1',
        title: t('agent.team-report.tabs.yesterday'),
      },
      {
        key: '3',
        title: t('agent.team-report.tabs.3days'),
      },
      {
        key: '7',
        title: t('agent.team-report.tabs.7days'),
      },
      {
        key: '15',
        title: t('agent.team-report.tabs.15days'),
      },
      {
        key: '30',
        title: t('agent.team-report.tabs.30days'),
      },
    ];
  }, [t]);

  const handleFilterValue = (value: string) => {
    setFilterValue(value);
    if (value === '') {
      return;
    }
    const perDayMs = 1000 * 60 * 60 * 24;
    if (value === '0') {
      setStartDate(Date.now());
      setEndDate(Date.now());
      return;
    }
    setStartDate(Date.now() - perDayMs * +value);
    setEndDate(Date.now() - perDayMs);
  };

  const handleDateConfirm = (range: {startDate: number; endDate: number}) => {
    const {startDate: s, endDate: e} = range;
    setStartDate(s);
    setEndDate(e);
  };

  useEffect(() => {
    setLoading(true);
    getTeamReportData({
      startDate: formatDate(startDate, 'yyyyMMdd'),
      endDate: formatDate(endDate, 'yyyyMMdd'),
    })
      .then(item => setPanel(item))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  return (
    <div className="flex flex-col py-5 px-3 gap-y-4 relative bg-white">
      <div className="flex flex-row gap-x-3 overflow-hidden">
        <Tabs
          className={cn(
            'flex flex-col justify-end flex-1 overflow-hidden',
            styles.teamReportPanelTab,
          )}
          activeKey={filterValue}
          activeLineMode="fixed"
          onChange={key => handleFilterValue(key as string)}>
          {tabOptions.map(option => (
            <Tabs.Tab key={option.key} title={option.title} />
          ))}
        </Tabs>
        <DateFilter
          startDate={startDate}
          endDate={endDate}
          onDateConfirm={handleDateConfirm}
        />
      </div>
      {loading ? (
        <div className="h-full w-full flex absolute justify-center items-center">
          <ProgressiveCircularLoading size={24} />
        </div>
      ) : null}
      <div className="px-3 flex flex-row justify-between items-center">
        <PanelSmallItem
          icon={<HomeNewUserIcon className="w-4 h-4 text-t2" />}
          loading={loading}
          title={t('agent.team-report.new-user')}
          result={panel ? panel?.newUserCount + '' : '-'}
        />
        <PanelSmallItem
          placement="right"
          icon={<HomeBetUserIcon className="w-4 h-4 text-t2" />}
          loading={loading}
          title={t('agent.team-report.recharge-user')}
          result={panel ? panel?.rechargeUserCount + '' : '-'}
        />
      </div>
      <div className="px-3 flex flex-row justify-between items-center">
        <div className="flex flex-col gap-y-2">
          <PanelSmallItem
            icon={<HomeRechargeIcon className="w-4 h-4 text-t2" />}
            loading={loading}
            title={t('agent.team-report.recharge')}
            result={panel ? toPriceStr(+panel.rechargeAmount) : '-'}
          />
          <PanelSmallItem
            loading={loading}
            title={t('agent.team-report.recharge-commission')}
            result={panel ? toPriceStr(+panel.rechargeCommissionAmount) : '-'}
          />
        </div>
        <div className="w-0 h-full border-l border-t2 opacity-30" />
        <div className="flex flex-col gap-y-2">
          <PanelSmallItem
            icon={<HomeBettingIcon className="w-4 h-4 text-t2" />}
            loading={loading}
            title={t('agent.team-report.betting')}
            result={panel ? toPriceStr(+panel.betAmount) : '-'}
          />
          <PanelSmallItem
            loading={loading}
            title={t('agent.team-report.betting-commission')}
            result={panel ? toPriceStr(+panel.betCommissionAmount) : '-'}
          />
        </div>
      </div>
    </div>
  );
};

export default TeamReportPanel;
